import { Box, Flex, Text } from "@chakra-ui/react";
import { debounce, get } from "lodash";
import React, { type FormEventHandler, type ReactNode, useCallback, useEffect, useState } from "react";
import { type AdditionalHubSpotFormField } from "lib/sanity";
import blockedEmails from "blocked-emails.json";
import { isValidEmail, searchEmail, submitHubSpotForm, isGdprUser } from "utils";
import * as Sentry from "@sentry/nextjs";
import { Button } from "ui/button";
import { Control } from "./control";

export type FormProps = {
  formId: string;
  sfdcCampaignId?: string;
  label: string;
  successContent?: ReactNode;
  displayedFields?: string[];
  additionalCustomFields?: AdditionalHubSpotFormField[];
  enableAutoComplete?: boolean; // eslint-disable-line react/boolean-prop-naming
  hideDisabledState?: boolean; // eslint-disable-line react/boolean-prop-naming
  legalText?: ReactNode;
  onSubmit?: (data: Record<string, string>) => void;
};

const CLEARBIT_TO_HUBSPOT = {
  "person.name.givenName": "firstname",
  "person.name.familyName": "lastname",
  "company.name": "company",
  "company.metrics.employees": "numberofemployees",
  "company.geo.country": "country",
  "company.geo.state": "state",
  "person.employment.title": "jobtitle",
  gdpr: "gdpr",
};

export function HubSpotForm({
  label,
  formId,
  sfdcCampaignId,
  displayedFields = ["firstname", "lastname", "company", "numberofemployees", "country", "state"],
  additionalCustomFields,
  successContent,
  enableAutoComplete = true,
  hideDisabledState,
  legalText,
  onSubmit,
}: FormProps) {
  const [fields, setFields] = useState<string[]>([]);
  const [email, setEmail] = useState<string>("");
  const [gdprOptIn, setGdprOptIn] = useState<boolean>(false);
  const [enriched, setEnriched] = useState<boolean>(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  const [gdprEnabled, setGdprEnabled] = useState<boolean>(false);

  const autoComplete = async (email: string) => {
    if (email && isValidEmail(email)) {
      setLoading(true);
      const profile = (await searchEmail(email)) as any;
      const enrichedValues: Record<string, string> = {};
      const neededFields: string[] = [];

      for (const [clearbitKey, hubspotKey] of Object.entries(CLEARBIT_TO_HUBSPOT)) {
        if (!displayedFields?.includes(hubspotKey)) {
          continue;
        }

        const value = get(profile, clearbitKey);
        if (value) {
          enrichedValues[hubspotKey] = value;
        } else {
          neededFields.push(hubspotKey);
          enrichedValues[hubspotKey] = "";
        }
      }

      setLoading(false);
      setEnriched(true);
      setValues(enrichedValues);
      setFields(neededFields);
    } else {
      setValues({});
      setFields([]);
    }
  };

  useEffect(() => {
    if (!enableAutoComplete) {
      const enrichedValues: Record<string, string> = {};
      const neededFields: string[] = [];

      for (const hubspotKey of Object.values(CLEARBIT_TO_HUBSPOT)) {
        if (!displayedFields?.includes(hubspotKey)) {
          continue;
        }

        neededFields.push(hubspotKey);
        enrichedValues[hubspotKey] = "";
      }

      setValues(enrichedValues);
      setFields(neededFields);
    }

    const fetchAndSetGdprStatus = async () => {
      setGdprEnabled(await isGdprUser());
    };

    fetchAndSetGdprStatus().catch((error) => {
      Sentry.captureException(error);
    });
  }, []);

  const debouncedAutoComplete = useCallback(debounce(autoComplete, 500), []);

  const submit: FormEventHandler<HTMLFormElement> = async (event) => {
    const pageUri = window.location.href;

    setSubmitting(true);
    event.preventDefault();
    try {
      // Safety check to make sure users always have firstname and lastname
      if (!values.lastname || !values.firstname) {
        // eslint-disable-next-line no-alert
        alert("Please enter both your first name and last name.");
        return;
      }

      const domain = email.split("@").pop();

      if (domain && blockedEmails.includes(domain)) {
        setError("Please provide a valid work email.");
        return;
      }

      const data = { email, ...values, ...customFields };

      await submitHubSpotForm(formId, data, pageUri, gdprEnabled, gdprOptIn, sfdcCampaignId);

      if (onSubmit) {
        onSubmit(data);
      }

      setSuccess(true);
      setError(undefined);
    } finally {
      setSubmitting(false);
    }
  };

  if (success && successContent) {
    return <Box>{successContent}</Box>;
  }

  const isLoading = loading || submitting;

  return (
    <form onSubmit={submit}>
      <Flex direction="column" gap="smaller">
        <Control
          key="email"
          country={values.country}
          name="email"
          value={email}
          onChange={async (value) => {
            setEmail(value);
            if (enableAutoComplete) {
              await debouncedAutoComplete(value);
            }
          }}
        />
        {fields.map((name) => (
          <Control
            key={name}
            country={values.country}
            name={name}
            value={values[name]}
            onChange={(value) => {
              setValues((previousValues) => ({ ...previousValues, [name]: value }));
            }}
          />
        ))}
        {additionalCustomFields?.map(({ fieldName, fieldLabel, inputType, required, fieldType, selectOptions }) => {
          return (
            <Control
              key={fieldName}
              isCustomField
              country={values.country}
              isRequired={required}
              label={fieldLabel}
              name={fieldName}
              type={inputType}
              fieldType={fieldType}
              options={selectOptions}
              value={customFields[fieldName]}
              onChange={(value) => {
                setCustomFields({ ...customFields, [fieldName]: value });
              }}
            />
          );
        })}
        {error && (
          <Text color="brand.error" textStyle="caption1">
            {error}
          </Text>
        )}
        {gdprEnabled && (
          <Control
            key="gdpr"
            country={values.country}
            name="gdpr"
            value="gdpr"
            onChange={() => {
              setGdprOptIn((previousGdpr) => !previousGdpr);
            }}
          />
        )}
        <Button
          type="submit"
          loadingText={label}
          isLoading={isLoading}
          disabled={isLoading || (enableAutoComplete && !enriched && !hideDisabledState)}
        >
          {label}
        </Button>
        {legalText}
      </Flex>
    </form>
  );
}
